import { useState, useCallback } from 'react';

interface NameInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  error?: string | null;
}

export function NameInput({
  value,
  onChange,
  onSubmit,
  isLoading = false,
  disabled = false,
  error,
}: NameInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && value.trim().length >= 2 && !isLoading && !disabled) {
        onSubmit();
      }
    },
    [value, isLoading, disabled, onSubmit]
  );

  return (
    <div className="space-y-2">
      <div
        className={`relative border-b-2 transition-colors ${
          isFocused
            ? 'border-editorial-black'
            : error
            ? 'border-danger'
            : 'border-gray-300'
        }`}
      >
        <div className="flex items-center">
          <div className="text-gray-400">
            <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
              search
            </span>
          </div>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="e.g., Snake Plant, Monstera..."
            disabled={disabled || isLoading}
            className="w-full bg-transparent px-3 py-3 font-serif text-editorial-black placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Plant name"
            aria-describedby={error ? 'name-error' : undefined}
          />
          {isLoading && (
            <div className="pr-1">
              <span
                className="material-symbols-outlined text-primary animate-pulse"
                style={{ fontSize: 20 }}
                aria-hidden="true"
              >
                hourglass_top
              </span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <p id="name-error" className="text-sm font-sans text-danger" role="alert">
          {error}
        </p>
      )}

      <p className="text-xs font-sans text-gray-400">
        Enter the common or scientific name of your plant
      </p>
    </div>
  );
}
